import { useLens } from '@hookform/lenses';
import { zodResolver } from '@hookform/resolvers/zod';
import { createFileRoute, Link, useBlocker } from '@tanstack/react-router';
import { FormProvider, type Path, useForm } from 'react-hook-form';
import { useIxfAccountingV1ManualEntryCommandServiceCreateManualEntry } from '@interxfi/api-client/generated/hooks/ixf-accounting-v1-manual-entry-command-service-create-manual-entry';
import { FormLayout } from '@interxfi/app-components/components/layout/action-layout/form-layout/form-layout';
import { DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE } from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { useButtonState } from '@interxfi/design-system/components/button/button-state';
import { useAddToast } from '@interxfi/design-system/components/toast/toast-context';
import { AccountingManualEntryCancelCreateAlertDialog } from '../../../../../../../../../features/accounting/manual-entry/accounting-manual-entry-cancel-create-alert-dialog';
import { ACCOUNTING_MANUAL_ENTRY_BASE_PATH } from '../../../../../../../../../features/accounting/manual-entry/accounting-manual-entry-routing';
import {
  AccountingManualEntrySpecForm,
  type AccountingManualEntrySpecFormValues,
  accountingManualEntrySpecFormDefaultValues,
  accountingManualEntrySpecFormOnSubmitSchema,
  accountingManualEntrySpecFormSchema,
  formValuesToAccountingManualEntrySpec,
} from '../../../../../../../../../features/accounting/manual-entry/form/accounting-manual-entry-spec-form';
import { buildDefaultErrorMessageGetter, useErrorHandler } from '../../../../../../../../../util/error-handler';
import { getPageHead } from '../../../../../../../../../util/routing/meta';

export const Route = createFileRoute('/_app/_clearing-core/_clearing-required/clearing/finance/_finance/journal/_manual-entry/create')({
  loader: () =>
    ({
      breadcrumb: buildBreadcrumb({
        id: 'new-manual-entry',
        label: 'New Manual Entry',
        to: `${ACCOUNTING_MANUAL_ENTRY_BASE_PATH}/create`,
      }),
    }) satisfies AggregateBreadcrumb,
  head: () => getPageHead('Create Manual Entry'),
  component: AccountingCreateManualEntry,
});

const errorMessageGetter = buildDefaultErrorMessageGetter('Failed to create manual entry', 'create a manual entry');

function AccountingCreateManualEntry() {
  const navigate = Route.useNavigate();
  const addToast = useAddToast();
  const { data, mutateAsync, isPending, error } = useIxfAccountingV1ManualEntryCommandServiceCreateManualEntry();
  useErrorHandler(error, errorMessageGetter);
  const [buttonState, buttonRef] = useButtonState(isPending, error);

  const form = useForm<AccountingManualEntrySpecFormValues>({
    mode: 'all',
    defaultValues: accountingManualEntrySpecFormDefaultValues,
    resolver: zodResolver(accountingManualEntrySpecFormSchema),
  });
  const {
    control,
    formState: { isDirty, isValid },
    handleSubmit,
    setError,
  } = form;
  const lens = useLens({ control });

  const onSubmit = async (values: AccountingManualEntrySpecFormValues) => {
    const { success, error } = accountingManualEntrySpecFormOnSubmitSchema.safeParse(values);
    if (!success) {
      error.issues?.forEach((issue) => {
        setError(issue.path.join('.') as Path<AccountingManualEntrySpecFormValues>, { message: issue.message });
      });

      addToast({
        id: 'createManualEntryError',
        variant: 'error',
        content: error.issues?.[0]?.message || 'There is a problem with the data you provided, please try again.',
      });

      return;
    }
    try {
      const res = await mutateAsync({ spec: formValuesToAccountingManualEntrySpec(values) });

      if (res?.manualEntry?.entryId) {
        addToast({ id: 'manualEntryCreated', content: 'Manual entry created', variant: 'success' });
        await navigate({ to: '/clearing/finance/journal/$entryId', params: { entryId: res.manualEntry.entryId } });
      }
    } catch {}
  };

  const {
    proceed,
    reset: stopNavigation,
    status,
  } = useBlocker({
    shouldBlockFn: ({ next }) => {
      if (isDirty && !data?.manualEntry) {
        return !next.fullPath.startsWith('/clearing/finance/journal/$entryId');
      }

      return false;
    },
    enableBeforeUnload: false,
    withResolver: true,
  });

  return (
    <FormProvider {...form}>
      <FormLayout
        actions={[
          {
            buttonLink: {
              id: 'cancel',
              children: 'Cancel',
              LinkElement: Link,
              to: '/clearing/finance/journal',
              variant: 'secondary',
            },
          },
          {
            button: {
              id: 'createManualEntry',
              children: 'Create Manual Entry',
              disabled: !isValid,
              ref: buttonRef,
              state: buttonState,
              type: 'submit',
              variant: 'primary',
            },
          },
        ]}
        maxWidthContainer={DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE}
        onSubmit={handleSubmit(onSubmit)}
      >
        <AccountingManualEntrySpecForm lens={lens} />
      </FormLayout>

      <AccountingManualEntryCancelCreateAlertDialog isOpen={status === 'blocked'} onCancel={stopNavigation} onConfirm={proceed} trigger={null} />
    </FormProvider>
  );
}
